import { Box, SxProps, Theme, Typography, useMediaQuery, useTheme } from '@mui/material'
import moment from 'moment'
import React, { Ref, useMemo, useRef, useState } from 'react'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts'
import { UnitTypes } from 'src/Libs/types'
import { useIsMobileLandscape } from 'src/Libs/flow/hooks'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ErrorSkeleton } from '@e3dc-react/shell/Elements/Error'
import { useChartTooltipConfig } from '../Dashboard/hooks/use-chart-tooltip-config'
import { EMobilityState } from '../EMobilityChargingSessions'
import { wallboxesSort } from '../utils/wallboxes-sort'
import { ChargingSessionChartTooltip } from './ChargingSessionChartTooltip'
import { ChargingSessionsChartLegend, WallboxLegendEntry } from './ChargingSessionsChartLegend'
import { getEMobilityChartShade } from './get-emobility-chart-shade'

const containerLarge = (isTooltipWider: boolean): SxProps<Theme> => ({
  '& .recharts-tooltip-wrapper': {
    /** Important needed for overwriting inline styling of the recharts library */
    transform: `translate(20px, -${isTooltipWider ? 87 : 60}px) !important`,
  },
  '& .recharts-surface': {
    pl: 2.5,
    overflow: 'visible',
  },
})

const containerSmall: SxProps<Theme> = {
  '& .recharts-wrapper': {
    display: 'flex',
    flexWrap: 'wrap',
    /** Important needed for overwriting inline styling of the recharts library */
    height: 'auto !important' as 'auto',
    maxHeight: 'unset !important' as 'auto',
  },
  '& .recharts-surface': {
    order: 1,
    paddingLeft: 2.5,
    overflow: 'visible',
  },
  '& .recharts-legend-wrapper': {
    /** Important needed for overwriting inline styling of the recharts library */
    position: 'static !important' as 'static',
    padding: '0 14px',
    order: 3,
  },
  '& .recharts-tooltip-wrapper': {
    /** Important needed for overwriting inline styling of the recharts library */
    position: 'static !important' as 'static',
    transform: 'unset !important' as 'unset',
    order: 2,
    marginBottom: 18,
  },
}

/**
 * We transform incoming charging sessions array to this data structure.
 *
 * This data structure is chosen so that each date displays a single vertically stacked bar
 * which consists of several smaller bars, each related to a single wallbox inside the "wallboxes"
 * array.
 */
export type ChartDataEntry = {
  date: string
  wallboxes: {
    [id: string]: {
      id: string
      deviceName: string
      grid: number
      solar: number
    }
  }
}

interface ChargingSessionChartProps {
  installationId: number
  data: ChartDataEntry[]
  displayEnergyAll: boolean
  state: EMobilityState
  toggleHiddenWallbox: (wallboxType: 'grid' | 'solar', wallboxId: string) => void
}
/**
 * Chart component for emobility charging sessions
 * requires data of grid, solar and date
 * it holds chart bars
 * it hold chart legend
 */
export const ChargingSessionChart: React.FC<ChargingSessionChartProps> = ({ data, displayEnergyAll, state, toggleHiddenWallbox }) => {
  const { t } = useCustomTranslation()

  const { palette, breakpoints } = useTheme()
  const downMd = useMediaQuery(breakpoints.down('md'))
  const downSm = useMediaQuery(breakpoints.down('sm'))

  const chartContainerRef = useRef<HTMLDivElement>(null)
  const [isTooltipWider, onMouseMoveOrEnter] = useChartTooltipConfig('.charging-sessions-table-tooltip', chartContainerRef.current)

  const isMobileLandscape = useIsMobileLandscape()

  const isSmallDisplay = downMd && !isMobileLandscape
  /* BarChart requires at least one data point in order to display a valid, but empty chart, so mock it here */
  const barChartData: ChartDataEntry[] = useMemo<ChartDataEntry[]>(() => {
    const result = data.length === 0 ? [{ date: '', wallboxes: {} }] : data
    /** The purpose of this is to add undefined values to the results in order to align the single bar from the chart
     * to the left side of the chart container.
     */
    if (result.length === 1 && result[0].date) result.length = 10

    return result
  }, [data])

  /** Get all distinct wallboxes from the data */
  const wallboxes: { id: string; deviceName: string }[] = useMemo(() => {
    /* Iterate into Map to remove duplicates */
    const wallboxesMap = new Map<string, { id: string; deviceName: string }>()
    for (const datum of data) {
      if (datum) {
        for (const wallbox of Object.values(datum.wallboxes)) {
          const { id, deviceName } = wallbox
          wallboxesMap.set(wallbox.id, { id, deviceName })
        }
      }
    }

    return Array.from(wallboxesMap.values()).sort(wallboxesSort)
  }, [data])

  /** A map to get a wallbox's shade from its id. */
  const wallboxShades: Record<string, { grid: string; solar: string }> = useMemo(() => {
    const shades: Record<string, { grid: string; solar: string }> = {}
    const numberOfWallboxes = wallboxes.length
    for (let index = 0; index < numberOfWallboxes; index++) {
      const wallbox = wallboxes[index]
      shades[wallbox.id] = {
        grid: getEMobilityChartShade({
          palette: 'grid',
          shadeCount: numberOfWallboxes,
          shadeIndex: index,
        }),
        solar: getEMobilityChartShade({
          palette: 'solar',
          shadeCount: numberOfWallboxes,
          shadeIndex: index,
        }),
      }
    }
    return shades
  }, [wallboxes])

  const { hiddenGridWallboxIds, hiddenSolarWallboxIds } = state

  const gridWallboxes: WallboxLegendEntry[] = useMemo(
    () =>
      wallboxes.map(({ id, deviceName }) => ({
        id,
        deviceName,
        shade: wallboxShades[id].grid,
      })) ?? [],
    [wallboxes, wallboxShades]
  )

  const solarWallboxes: WallboxLegendEntry[] = useMemo(
    () =>
      wallboxes.map(({ id, deviceName }) => ({
        id,
        deviceName,
        shade: wallboxShades[id].solar,
      })) ?? [],
    [wallboxes, wallboxShades]
  )

  const [customPayload, setCustomPayload] = useState<Record<number, string>[] | null>(null)

  return (
    <>
      <Box sx={{ pt: 1, minHeight: downSm ? 70 : 140 }}>
        <Typography
          variant="h1"
          sx={{
            mx: 3,
            py: 1,
            borderBottom: `1px solid ${palette.divider}`,
          }}
        >
          {t('emobility.chargingSession.cumulativeEnergy')}
        </Typography>
      </Box>

      {data.length === 0 ? (
        <ErrorSkeleton title={t('common.noData')} type="barChart" />
      ) : (
        <>
          <Box sx={{ overflow: downSm ? 'scroll' : undefined }}>
            <Box sx={isSmallDisplay ? containerSmall : containerLarge(isTooltipWider)} height={'min-content'}>
              <ResponsiveContainer
                ref={chartContainerRef as Ref<HTMLDivElement> | undefined}
                width="99%"
                /* Keep 70 pixels for the y-axis and 4 pixels per bar */
                minWidth={downSm ? `${4 * barChartData.length + 70}px` : undefined}
                height={downSm ? 300 : 400}
                debounce={200}
              >
                <BarChart
                  margin={{
                    top: isSmallDisplay ? 0 : 32,
                  }}
                  data={barChartData}
                  onMouseMove={e => {
                    onMouseMoveOrEnter()

                    if (!downMd) return

                    setCustomPayload(e.activePayload ? e.activePayload.filter(payload => payload?.value !== undefined) : null)
                  }}
                  onMouseEnter={onMouseMoveOrEnter}
                  onMouseLeave={() => setCustomPayload(null)}
                >
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="date"
                    interval="equidistantPreserveStart"
                    tickSize={10}
                    fontSize={11}
                    padding={{ left: 12, right: 12 }}
                    tickFormatter={date => (date ? moment(date).format('DD.MM') : '')}
                  />
                  <YAxis
                    tickSize={10}
                    fontSize={11}
                    // \xa0 - non breaking whitespace character (like the &nbsp; entity)
                    unit={`\xa0${UnitTypes.kiloWattHour}`}
                    tickCount={5}
                    domain={([_, dataMax]) => {
                      if (dataMax <= 1) return [0, Math.ceil(dataMax * 100) / 100] // 0.0121 => 0.02
                      if (dataMax <= 10) return [0, Math.ceil(dataMax * 10) / 10] // 3.41 => 3.5
                      return [0, Math.ceil(dataMax / 10) * 10] // 34 => 40, 1001 => 1010
                    }}
                  />

                  <Tooltip
                    position={{ x: 20, y: -60 }}
                    content={
                      downMd ? (
                        <></>
                      ) : (
                        (props: TooltipProps<number, string>) => (
                          <ChargingSessionChartTooltip
                            {...props}
                            displayEnergyAll={displayEnergyAll}
                            wallboxShades={wallboxShades}
                            hiddenGridWallboxIds={hiddenGridWallboxIds}
                            hiddenSolarWallboxIds={hiddenSolarWallboxIds}
                          />
                        )
                      )
                    }
                  />

                  {gridWallboxes.map((wallbox, index) => (
                    <Bar
                      key={`${index}-${wallbox.id}-grid`}
                      dataKey={`wallboxes[${wallbox.id}].grid`}
                      stackId="a"
                      fill={wallbox.shade}
                      hide={hiddenGridWallboxIds.includes(wallbox.id)}
                      isAnimationActive={false}
                    />
                  ))}
                  {solarWallboxes.map((wallbox, index) => (
                    <Bar
                      key={`${index}-${wallbox.id}-solar`}
                      dataKey={`wallboxes[${wallbox.id}].solar`}
                      stackId="a"
                      fill={wallbox.shade}
                      hide={hiddenSolarWallboxIds.includes(wallbox.id)}
                      isAnimationActive={false}
                    />
                  ))}

                  {/* The legend's height is subtracted from the chart in a vertical layout, so
            it is omitted in the small view and is rendered as a separate component */}
                  {!isSmallDisplay && (
                    <Legend
                      align="right"
                      verticalAlign="top"
                      layout={isSmallDisplay ? 'horizontal' : 'vertical'}
                      wrapperStyle={{
                        right: -10,
                        top: downSm ? 125 : 20,
                        maxWidth: isSmallDisplay ? undefined : '40%',
                      }}
                      content={
                        <ChargingSessionsChartLegend
                          displayEnergyAll={displayEnergyAll}
                          gridWallboxes={gridWallboxes}
                          solarWallboxes={solarWallboxes}
                          hiddenGridWallboxIds={hiddenGridWallboxIds}
                          hiddenSolarWallboxIds={hiddenSolarWallboxIds}
                          toggleHiddenWallbox={toggleHiddenWallbox}
                          smallDisplay={isSmallDisplay}
                        />
                      }
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>

          {downMd && customPayload ? (
            <Box>
              <ChargingSessionChartTooltip
                active
                payload={customPayload}
                displayEnergyAll={displayEnergyAll}
                wallboxShades={wallboxShades}
                hiddenGridWallboxIds={hiddenGridWallboxIds}
                hiddenSolarWallboxIds={hiddenSolarWallboxIds}
              />
            </Box>
          ) : null}

          {isSmallDisplay && (
            <Box px={2}>
              <ChargingSessionsChartLegend
                displayEnergyAll={displayEnergyAll}
                gridWallboxes={gridWallboxes}
                solarWallboxes={solarWallboxes}
                hiddenGridWallboxIds={hiddenGridWallboxIds}
                hiddenSolarWallboxIds={hiddenSolarWallboxIds}
                toggleHiddenWallbox={toggleHiddenWallbox}
                smallDisplay={isSmallDisplay}
              />
            </Box>
          )}
        </>
      )}
    </>
  )
}
